import { createContext, useContext, type ReactNode } from 'react'

import type { CustomerData, UpdateCustomerPayload } from '../types/customer'
import type { UseMutationResult } from '@tanstack/react-query'

interface CustomerDataContextValue {
  customerData: CustomerData | null
  updateCustomer: (payload: UpdateCustomerPayload) => void
  updateCustomerAsync: (payload: UpdateCustomerPayload) => Promise<CustomerData>
  isUpdating: boolean
  updateError: Error | null
}

const CustomerDataContext = createContext<CustomerDataContextValue | null>(null)

interface CustomerDataProviderProps {
  children: ReactNode
  customerData: CustomerData | null
  updateMutation: UseMutationResult<
    CustomerData,
    Error,
    UpdateCustomerPayload,
    unknown
  >
}

export function CustomerDataProvider({
  children,
  customerData,
  updateMutation,
}: CustomerDataProviderProps) {
  const value: CustomerDataContextValue = {
    customerData,
    updateCustomer: updateMutation.mutate,
    updateCustomerAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  }

  return (
    <CustomerDataContext.Provider value={value}>
      {children}
    </CustomerDataContext.Provider>
  )
}

export function useCustomerData() {
  const context = useContext(CustomerDataContext)
  if (!context) {
    throw new Error('useCustomerData must be used within CustomerDataProvider')
  }
  return context
}
